import React, { useEffect, useRef, useState, useCallback } from 'react';
import EvidenceLayer from '../../components/EvidenceLayer';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { evaluationsAPI, projectsAPI, locationsAPI } from '../../api/client';
import { mapPointsAPI, photoUrl } from '../../api/mapPoints';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Plus, Trash2, Edit2, X, Save, Upload, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const MEXICO_CENTER = { lat: 19.4326, lng: -99.1332 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const MARKER_CONFIG = {
  hospital:   { label: 'Hospital',           color: '#15803D', symbol: 'H',  bg: 'bg-green-700'  },
  evacuacion: { label: 'Ruta de Evacuación', color: '#1A56DB', symbol: 'E',  bg: 'bg-blue-700'   },
  riesgo:     { label: 'Zona de Riesgo',     color: '#D97706', symbol: 'R',  bg: 'bg-orange-500' },
  critico:    { label: 'Punto Crítico',      color: '#DC2626', symbol: '!',  bg: 'bg-red-600'    },
  location:   { label: 'Locación',           color: '#1A56DB', symbol: '📍', bg: 'bg-blue-700'   },
};
const CATEGORIES = Object.entries(MARKER_CONFIG).map(([k, v]) => ({ id: k, ...v }));

function getMarkerSvgUrl(category) {
  const cfg = MARKER_CONFIG[category] || MARKER_CONFIG.critico;
  const c = cfg.color.replace(/#/g, '%23');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30'><circle cx='15' cy='15' r='13' fill='${c}' stroke='white' stroke-width='2.5'/><text x='15' y='20' text-anchor='middle' fill='white' font-size='13' font-weight='bold' font-family='Arial,sans-serif'>${cfg.symbol}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${svg}`;
}

function MarkerIcon({ category, size = 'md' }) {
  const cfg = MARKER_CONFIG[category] || MARKER_CONFIG.critico;
  const sz = size === 'sm' ? 'w-5 h-5 text-xs' : 'w-7 h-7 text-sm';
  return (
    <span className={`${sz} ${cfg.bg} text-white rounded-full flex items-center justify-center font-black flex-shrink-0`}>
      {cfg.symbol}
    </span>
  );
}

function MarkerModal({ mode, latLng, marker, onSave, onDelete, onClose, saving }) {
  const [form, setForm] = useState({
    category: marker?.category || 'hospital',
    notes: marker?.notes || '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(
    marker?.photo_url ? photoUrl(marker.photo_url) : null
  );
  const fileRef = useRef(null);
  const coords = latLng || { lat: marker?.lat, lng: marker?.lng };

  const handlePhoto = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-soem-navy rounded-t-sm">
          <div>
            <h3 className="font-bold text-white">{mode === 'add' ? 'Agregar Marcador' : 'Editar Marcador'}</h3>
            {coords?.lat && (
              <p className="text-slate-400 text-xs mt-0.5 font-mono">
                {coords.lat?.toFixed(5)}, {coords.lng?.toFixed(5)}
              </p>
            )}
          </div>
          <button aria-label="Cerrar" onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(form, photoFile); }} className="p-5 space-y-4">
          {/* Category */}
          <div>
            <label htmlFor="f-categoría" className="soem-label block mb-2">Categoría</label>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map(cat => (
                <button key={cat.id} type="button"
                  onClick={() => setForm(f => ({ ...f, category: cat.id }))}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border-2 text-sm font-semibold transition-all ${
                    form.category === cat.id ? 'text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  style={form.category === cat.id ? { backgroundColor: cat.color, borderColor: cat.color } : {}}
                  data-testid={`cat-${cat.id}`}>
                  <MarkerIcon category={cat.id} size="sm" /> {cat.label}
                </button>
              ))}
            </div>
          </div>
          {/* Notes */}
          <div>
            <label htmlFor="f-notas" className="soem-label block mb-1.5">Notas</label>
            <textarea
id="f-notas"               className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary resize-none"
              rows={3} placeholder="Observaciones del punto..."
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="marker-notes" />
          </div>
          {/* Photo */}
          <div>
            <label htmlFor="f-foto-del-lugar" className="soem-label block mb-1.5">Foto del lugar</label>
            <input id="f-foto-del-lugar" ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="preview" className="w-full h-32 object-cover rounded-sm border border-slate-200" />
                <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                  className="absolute top-1.5 right-1.5 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700">
                  <X size={12} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-sm py-4 text-slate-400 hover:border-soem-primary hover:text-soem-primary transition-colors flex items-center justify-center gap-2 text-sm"
                data-testid="upload-photo-btn">
                <Upload size={16} /> Subir foto
              </button>
            )}
          </div>
          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="save-marker-btn">
              <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
            {mode === 'edit' && (
              <button aria-label="Eliminar" type="button" onClick={onDelete} disabled={saving}
                className="soem-btn-danger px-4 py-2.5 text-sm flex items-center justify-center disabled:opacity-60"
                data-testid="delete-marker-btn">
                <Trash2 size={14} />
              </button>
            )}
            <button type="button" onClick={onClose} className="soem-btn-secondary px-4 py-2.5 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EvaluationMap() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeUser } = useAuth();

  const [evaluation, setEvaluation] = useState(null);
  const [points, setPoints] = useState([]);
  const [mapCenter, setMapCenter] = useState(MEXICO_CENTER);
  const [loading, setLoading] = useState(true);
  const [mapInstance, setMapInstance] = useState(null);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [locationPin, setLocationPin] = useState(null);

  // Load evaluation + points + location
  useEffect(() => {
    async function load() {
      try {
        const [evalRes, pointsRes] = await Promise.all([
          evaluationsAPI.get(id),
          mapPointsAPI.list(id),
        ]);
        setEvaluation(evalRes.data);
        setPoints(pointsRes.data || []);
        
        // Try to get location coordinates
        let locCoords = null;
        
        // First try from location_info
        if (evalRes.data?.location_info?.lat && evalRes.data?.location_info?.lng) {
          locCoords = {
            lat: parseFloat(evalRes.data.location_info.lat),
            lng: parseFloat(evalRes.data.location_info.lng),
            name: evalRes.data.location_info.name || 'Locación'
          };
        }
        
        // If not in location_info, try to get from project location
        if (!locCoords && evalRes.data?.project_id) {
          try {
            const projRes = await projectsAPI.get(evalRes.data.project_id);
            if (projRes.data?.location_id) {
              const locRes = await locationsAPI.get(projRes.data.location_id);
              if (locRes.data?.gps_location?.lat && locRes.data?.gps_location?.lng) {
                locCoords = {
                  lat: parseFloat(locRes.data.gps_location.lat),
                  lng: parseFloat(locRes.data.gps_location.lng),
                  name: locRes.data.name || 'Locación'
                };
              }
            }
          } catch { /* ignore */ }
        }
        
        if (locCoords) {
          setLocationPin(locCoords);
          setMapCenter({ lat: locCoords.lat, lng: locCoords.lng });
        }
      } catch { toast.error('Error al cargar datos'); }
      finally { setLoading(false); }
    }
    load();
  }, [id]);

  // Re-center the live map instance as soon as we know the location pin
  // (#p8figBzR — el mapa debía abrir centrado en la locación, no en CDMX).
  useEffect(() => {
    if (mapInstance && locationPin) {
      mapInstance.setCenter({ lat: locationPin.lat, lng: locationPin.lng });
      mapInstance.setZoom(16);
    }
  }, [mapInstance, locationPin]);

  // Geocode ubicacion (fallback cuando la locación no tiene gps_location).
  // Acepta tanto location_info.address como general_fields.ubicacion.
  useEffect(() => {
    if (locationPin) return; // ya tenemos coordenadas, no necesitamos geocodear
    if (!mapInstance || !evaluation) return;
    if (!window.google) return;
    const baseAddress =
      evaluation.location_info?.address ||
      evaluation.general_fields?.ubicacion ||
      evaluation.location_info?.name;
    if (!baseAddress) return;
    // Concatenar el nombre y forzar país MX para evitar que CDMX
    // geocodifique a Madrid u otra ubicación homónima fuera de México.
    const name = evaluation.location_info?.name;
    const addressText = [name, baseAddress, 'México'].filter(Boolean).join(', ');
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: addressText, componentRestrictions: { country: 'MX' } },
      (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          const center = { lat: loc.lat(), lng: loc.lng() };
          setMapCenter(center);
          setLocationPin({ lat: center.lat, lng: center.lng, name: name || 'Locación' });
          mapInstance.setCenter(center);
          mapInstance.setZoom(16);
        }
      },
    );
  }, [mapInstance, evaluation, locationPin]);

  const handleMapClick = useCallback((e) => {
    setModal({ mode: 'add', latLng: { lat: e.latLng.lat(), lng: e.latLng.lng() } });
  }, []);

  const handleSave = async (form, photoFile) => {
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        const res = await mapPointsAPI.create({
          evaluation_id: id,
          lat: modal.latLng.lat,
          lng: modal.latLng.lng,
          category: form.category,
          notes: form.notes || null,
          created_by: activeUser?.id || null,
        });
        let newPoint = res.data;
        if (photoFile) {
          const photoRes = await mapPointsAPI.uploadPhoto(newPoint.id, photoFile);
          newPoint = photoRes.data;
        }
        setPoints(prev => [...prev, newPoint]);
        toast.success('Marcador agregado');
      } else {
        let updated = modal.marker;
        const updateRes = await mapPointsAPI.update(updated.id, {
          category: form.category,
          notes: form.notes || null,
        });
        updated = updateRes.data;
        if (photoFile) {
          const photoRes = await mapPointsAPI.uploadPhoto(updated.id, photoFile);
          updated = photoRes.data;
        }
        setPoints(prev => prev.map(p => p.id === updated.id ? updated : p));
        toast.success('Marcador actualizado');
      }
      setModal(null);
      setSelectedPoint(null);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar marcador');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!modal?.marker) return;
    setSaving(true);
    try {
      await mapPointsAPI.remove(modal.marker.id);
      setPoints(prev => prev.filter(p => p.id !== modal.marker.id));
      setModal(null);
      setSelectedPoint(null);
      toast.success('Marcador eliminado');
    } catch { toast.error('Error al eliminar marcador'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-soem-bg">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-soem-primary border-t-transparent" />
    </div>
  );

  const evalName = evaluation?.general_fields?.instalacion || `Evaluación v${evaluation?.version}`;
  const ubicacion = evaluation?.general_fields?.ubicacion;

  return (
    <div className="flex flex-col h-screen bg-soem-bg" data-testid="evaluation-map-page">
      {/* Header */}
      <div className="bg-soem-navy px-5 py-3 flex items-center justify-between flex-shrink-0 shadow-md z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/dashboard/evaluaciones/${id}/checklist`)}
            className="text-slate-400 hover:text-white p-1.5 rounded-sm hover:bg-white/10 transition-colors"
            data-testid="back-to-checklist-btn">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-white font-bold text-sm">{evalName} — Mapa de la Locación</h1>
            {ubicacion && (
              <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1">
                <MapPin size={10} /> {ubicacion}
              </p>
            )}
          </div>
        </div>
        <span className="bg-soem-primary text-white text-xs font-bold px-2.5 py-1 rounded-full">
          {points.length} marcador{points.length !== 1 ? 'es' : ''}
        </span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Map */}
        <div className="flex-1 relative" data-testid="map-container">
          <LoadScript googleMapsApiKey={MAPS_KEY} libraries={['places']}>
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={mapCenter}
              zoom={15}
              onClick={handleMapClick}
              onLoad={map => setMapInstance(map)}
              options={{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true,
                styles: [
                  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
                  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
                  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
                  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9d2d3' }] },
                  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                ],
              }}
            >
              {/* Location pin - always shown as base layer */}
              {locationPin && (
                <Marker
                  position={{ lat: locationPin.lat, lng: locationPin.lng }}
                  title={locationPin.name}
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><circle cx='20' cy='20' r='17' fill='%231A56DB' stroke='white' stroke-width='3'/><text x='20' y='27' text-anchor='middle' fill='white' font-size='18' font-weight='bold' font-family='Arial,sans-serif'>📍</text></svg>`)}`,
                    scaledSize: new window.google.maps.Size(40, 40),
                  }}
                  zIndex={1000}
                />
              )}
              
              {points.map(point => (
                  <Marker
                    key={point.id}
                    position={{ lat: point.lat, lng: point.lng }}
                    title={MARKER_CONFIG[point.category]?.label}
                    icon={getMarkerSvgUrl(point.category)}
                    onClick={() => setSelectedPoint(selectedPoint?.id === point.id ? null : point)}
                  />
                ))}

              {selectedPoint && (
                <InfoWindow
                  position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
                  onCloseClick={() => setSelectedPoint(null)}
                >
                  <div className="min-w-[160px]">
                    <div className="flex items-center gap-2 mb-1">
                      <MarkerIcon category={selectedPoint.category} size="sm" />
                      <strong className="text-sm text-soem-navy">
                        {MARKER_CONFIG[selectedPoint.category]?.label}
                      </strong>
                    </div>
                    {selectedPoint.notes && (
                      <p className="text-xs text-slate-600 mb-2">{selectedPoint.notes}</p>
                    )}
                    {selectedPoint.photo_url && (
                      <img src={photoUrl(selectedPoint.photo_url)} alt="marker"
                        className="w-full h-20 object-cover rounded mb-2" />
                    )}
                    <button
                      onClick={() => { setModal({ mode: 'edit', marker: selectedPoint }); setSelectedPoint(null); }}
                      className="text-xs text-soem-primary font-semibold hover:underline flex items-center gap-1"
                    >
                      <Edit2 size={11} /> Editar marcador
                    </button>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </LoadScript>

          {/* Floating instruction */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-soem-navy/90 text-white text-xs px-4 py-2 rounded-full shadow-lg flex items-center gap-2 pointer-events-none z-10">
            <Plus size={12} /> Haz clic en el mapa para agregar un marcador
          </div>
        </div>

        {/* Right panel */}
        <div className="w-72 flex-shrink-0 bg-white border-l border-slate-200 flex flex-col overflow-hidden">
          {/* Legend */}
          <div className="p-4 border-b border-slate-100">
            <p className="soem-label mb-3">Categorías</p>
            <div className="space-y-2">
              {CATEGORIES.map(cat => (
                <div key={cat.id} className="flex items-center gap-2.5">
                  <MarkerIcon category={cat.id} size="sm" />
                  <span className="text-sm text-soem-navy font-medium">{cat.label}</span>
                  <span className="ml-auto text-xs font-mono text-slate-400">
                    {points.filter(p => p.category === cat.id).length}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Markers list */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-slate-100">
              <p className="soem-label">Marcadores ({points.length})</p>
            </div>
            {points.length === 0 ? (
              <div className="p-6 text-center">
                <MapPin size={32} className="text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">Sin marcadores aún.</p>
                <p className="text-slate-400 text-xs mt-1">Haz clic en el mapa para agregar uno.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {points.map(point => {
                  const cfg = MARKER_CONFIG[point.category];
                  return (
                    <div key={point.id}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-blue-50/40 cursor-pointer transition-colors"
                      onClick={() => setModal({ mode: 'edit', marker: point })}
                      data-testid={`marker-item-${point.id}`}>
                      <MarkerIcon category={point.category} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-soem-navy">{cfg?.label}</p>
                        {point.notes && <p className="text-xs text-slate-500 truncate mt-0.5">{point.notes}</p>}
                        <p className="text-xs font-mono text-slate-300 mt-0.5">
                          {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                        </p>
                        {point.photo_url && (
                          <span className="text-xs text-soem-primary mt-0.5 flex items-center gap-1">
                            <Upload size={9} /> Foto adjunta
                          </span>
                        )}
                      </div>
                      <button onClick={e => { e.stopPropagation(); setModal({ mode: 'edit', marker: point }); }}
                        className="text-slate-300 hover:text-soem-primary flex-shrink-0">
                        <Edit2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Back button */}
          <div className="p-4 border-t border-slate-100">
            <button onClick={() => navigate(`/dashboard/evaluaciones/${id}/checklist`)}
              className="w-full soem-btn-primary py-2.5 text-sm flex items-center justify-center gap-2"
              data-testid="done-btn">
              <ArrowLeft size={14} /> Volver al Checklist
            </button>
          </div>
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto shrink-0"><EvidenceLayer evaluationId={id} map={mapInstance} /></div>
      {modal && (
        <MarkerModal
          mode={modal.mode}
          latLng={modal.latLng}
          marker={modal.marker}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}
